import { useRateLimitWait } from '@/hooks/use-rate-limit-wait';
import { formatRateLimitWait } from '@/lib/format/duration/rate-limit-wait';
import type { RateLimitBudget } from '@/lib/rate-limit-guard';

interface RetryActionOptions {
  /** What the control says when it may go: "Retry", "Check again". */
  label: string;
  /** What it says while the retry runs. Defaults to `label`. */
  pendingLabel?: string;
  /**
   * What it says while the rate limit is spent, given the wait ("40 s").
   * Defaults to "Try again in 40 s"; a control that is not a retry (a
   * Refresh) says it in its own words.
   */
  waitLabel?: (wait: string) => string;
  onRetry: () => void;
  isRetrying: boolean;
  /**
   * The limiter budget the retried request draws on: the global limiter
   * unless its route has a budget of its own.
   */
  budget?: RateLimitBudget;
}

/**
 * The one behaviour every retry control shares, in the shape a
 * `StatePanel` action takes (and a `Button` reads just as well).
 *
 * After a failure that was not a rate limit, the retry goes at once. While
 * the backend's rate limit for the request's budget is spent, it waits and
 * counts down ("Try again in 40 s"), then comes back on its own. It never
 * lifts the guard: clicking into a spent budget only spends it further,
 * and the backend has said when it will answer again.
 */
export function useRetryAction({
  label,
  pendingLabel = label,
  waitLabel = (wait) => `Try again in ${wait}`,
  onRetry,
  isRetrying,
  budget
}: RetryActionOptions) {
  const wait = useRateLimitWait(budget);
  const waiting = wait > 0;

  return {
    label: isRetrying
      ? pendingLabel
      : waiting
        ? waitLabel(formatRateLimitWait(wait))
        : label,
    // Disabled already; this also holds a click that reaches it anyway.
    onClick: waiting ? () => undefined : onRetry,
    disabled: isRetrying || waiting,
    loading: isRetrying
  };
}
